import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { Exam, ExamsApi } from '../api/exams'

/**
 * Settings > Exam Settings: which Exams students can reach, what each calls its parts, and the
 * Guarantee its passes are sold under, every Brand's on one page.
 *
 * Under the cog rather than under an Exam, because none of it is work on one Exam's content: turning
 * an Exam off takes it out of the App for every student at once, and a checkbox among its Sittings
 * was one stray click from doing so. Disabling asks first; enabling does not, since it hides nothing.
 *
 * THE WORDS ARE THE EXAM'S. TMUA sits Papers and ESAT Modules, and the code a part carries (P1, M1)
 * is the Bank's and need not match what a Student reads. Every screen in the App says what is set
 * here, so a second Brand's wording is an edit on this page and not a release.
 */
export function ExamSettings({ exams }: { exams: ExamsApi }) {
  const catalogue = useQuery({ queryKey: ['exams'], queryFn: () => exams.list() })

  if (catalogue.isPending) return <p className="text-muted">Reading the Exams...</p>
  if (catalogue.isError) {
    return (
      <p role="alert" className="border-l-2 border-red-700 pl-3 text-red-700">
        {catalogue.error.message}
      </p>
    )
  }

  const enabled = catalogue.data.filter((exam) => exam.enabled).length

  return (
    <ul className="max-w-2xl divide-y divide-ink-100 rounded-lg border border-ink-100 bg-card">
      {catalogue.data.map((exam) => (
        <li key={exam.code} className="p-4">
          <Availability exam={exam} exams={exams} last={exam.enabled && enabled === 1} />
          <Wording exam={exam} exams={exams} />
        </li>
      ))}
    </ul>
  )
}

function Availability({ exam, exams, last }: { exam: Exam; exams: ExamsApi; last: boolean }) {
  const client = useQueryClient()
  const availability = useMutation({
    mutationFn: (enabled: boolean) => exams.setAvailability(exam.code, enabled),
    onSuccess: () => client.invalidateQueries({ queryKey: ['exams'] }),
  })

  const change = (enabled: boolean) => {
    if (!enabled && !window.confirm(warning(exam, last))) return
    availability.mutate(enabled)
  }

  return (
    <>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={exam.enabled} disabled={availability.isPending}
          onChange={(event) => change(event.target.checked)} />
        Enable {exam.code} for students
      </label>
      <p className="mt-1 text-sm text-muted">{exam.name} &middot; {exam.brandCode}</p>
      {availability.error && <p role="alert" className="mt-2 text-red-700">{availability.error.message}</p>}
    </>
  )
}

const FIELD = 'mt-1 block w-full rounded-md border border-ink-200 bg-page px-3 py-2'

/**
 * What the Exam calls its parts and each part, and its Guarantee, saved together. Each part keeps
 * its code; its name is what a Student reads ("Paper 1", "Mathematics 1"), and its title, where the
 * Exam gives one, what it examines.
 */
function Wording({ exam, exams }: { exam: Exam; exams: ExamsApi }) {
  const client = useQueryClient()
  const [part, setPart] = useState(exam.wording?.part ?? '')
  const [parts, setParts] = useState(exam.wording?.parts ?? '')
  const [names, setNames] = useState(() => exam.components.map((each) => ({ code: each.code, name: each.name, title: each.title ?? '' })))
  const [days, setDays] = useState(String(exam.refund?.days ?? ''))
  const [questions, setQuestions] = useState(String(exam.refund?.questions ?? ''))
  const save = useMutation({
    mutationFn: async () => {
      await exams.setWording(exam.code, {
        part, parts,
        components: names.map((each) => ({ code: each.code, name: each.name, title: each.title.trim() || null })),
      })
      return exams.setRefund(exam.code, { days: Number(days), questions: Number(questions) })
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['exams'] }),
  })
  const rename = (code: string, change: Partial<{ name: string; title: string }>) =>
    setNames((held) => held.map((each) => (each.code === code ? { ...each, ...change } : each)))

  return (
    <form aria-label={`${exam.code} wording`} className="mt-4 space-y-4"
      onSubmit={(event) => { event.preventDefault(); save.mutate() }}>
      <fieldset className="grid gap-4 sm:grid-cols-2">
        <legend className="mb-2 font-bold">What {exam.code} calls its parts</legend>
        <label className="block">One part
          <input className={FIELD} required maxLength={32} placeholder="Paper" value={part} onChange={(event) => setPart(event.target.value)} />
        </label>
        <label className="block">Several parts
          <input className={FIELD} required maxLength={32} placeholder="Papers" value={parts} onChange={(event) => setParts(event.target.value)} />
        </label>
      </fieldset>
      {names.length > 0 && (
        <fieldset className="space-y-3">
          <legend className="mb-2 font-bold">Each {part.trim() || 'part'}</legend>
          {names.map((each) => (
            <div key={each.code} className="grid gap-3 sm:grid-cols-[4rem_1fr_2fr] sm:items-end">
              <span className="font-mono text-sm text-muted">{each.code}</span>
              <label className="block">Name of {each.code}
                <input className={FIELD} required maxLength={64} value={each.name} onChange={(event) => rename(each.code, { name: event.target.value })} />
              </label>
              <label className="block">Title of {each.code}
                <input className={FIELD} maxLength={128} placeholder="None" value={each.title} onChange={(event) => rename(each.code, { title: event.target.value })} />
              </label>
            </div>
          ))}
        </fieldset>
      )}
      <fieldset className="grid gap-4 sm:grid-cols-2">
        <legend className="mb-2 font-bold">Refund guarantee</legend>
        <label className="block">Days to cancel in
          <input className={FIELD} required type="number" min={1} value={days} onChange={(event) => setDays(event.target.value)} />
        </label>
        <label className="block">Questions opened that end it
          <input className={FIELD} required type="number" min={1} value={questions} onChange={(event) => setQuestions(event.target.value)} />
        </label>
      </fieldset>
      <button type="submit" className="rounded-md border border-green-800 px-4 py-2 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55" disabled={save.isPending}>
        {save.isPending ? 'Saving...' : `Save ${exam.code}`}
      </button>
      {save.isSuccess && <p role="status">Saved</p>}
      {save.isError && <p role="alert" className="text-red-700">{save.error.message}</p>}
    </form>
  )
}

function warning(exam: Exam, last: boolean) {
  const gone = `Disable ${exam.code}? Students will no longer be able to open it in the App, sign up for it, or practise it.`

  return last ? `${gone} It is the only Exam enabled, so the App will show every student "Page not found".` : gone
}
